import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Box, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';

import Button, { BUTTON_VARIANT } from '../../../../../../main-deprecated/components/Button';
import ConfirmationDialog from '../../../../../../main-deprecated/components/Dialog';
import CheckBox from '../../../../../components/CheckBox/CheckBox';
import {
  selectIsProcessingDocumentsApproved,
  selectIsProcessingEVerify,
} from '../../../../../store/selectors/userListSelector';
import { BLACK } from '../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../theme/fontConstants';
import theme from '../../../../../theme/theme';
import { updateDocumentsApproved, updateEVerify } from '../../../usersListReducer';

import SageWorkerControls from './SageWorkerControls';
import SectionCard from './SectionCard';

const styles = {
  checkBoxLabel: {
    color: BLACK[100],
    fontSize: '16px',
    fontFamily: PRIMARY_FONT[400],
  },
  nameStyle: {
    fontSize: '10px',
    my: 0.5,
    marginLeft: 4,
  },
};

const WAEOnboardingStatusCard = ({
  onboardingDoc,
  eVerify,
  sageId,
  sageEVerify,
  selectedUserId,
}) => {
  const dispatch = useDispatch();
  const isProcessingDocumentsApproved = useSelector(selectIsProcessingDocumentsApproved);
  const isProcessingEVerify = useSelector(selectIsProcessingEVerify);

  const [valueForModal, setValueForModal] = useState(null);

  const getEVerifiedByName = (val) => val?.verifiedByName || '--';

  const dispatchChange = useCallback(
    (val) => {
      if ('onboardingDoc' in val) {
        dispatch(
          updateDocumentsApproved({ workerId: selectedUserId, isApproved: val.onboardingDoc })
        );
      } else {
        dispatch(updateEVerify({ userId: selectedUserId, eVerify: val.eVerify }));
      }
    },
    [dispatch, selectedUserId]
  );

  const handleOnChange = useCallback(
    (val) => (onboardingDoc && eVerify ? setValueForModal(val) : dispatchChange(val)),
    [onboardingDoc, eVerify, dispatchChange]
  );
  const handleOnUnConfirm = useCallback(() => {
    dispatchChange(valueForModal);
    setValueForModal(null);
  }, [setValueForModal, dispatchChange, valueForModal]);
  const body = (
    <Grid container>
      <Typography
        sx={{
          color: theme?.candidate?.components?.readyToWorkUnConfirmModal?.typography?.body?.color,
        }}
      >
        {LanguageConverter('user.onboardingDocs.unconfirm.body')}
      </Typography>
    </Grid>
  );

  const dialogButtons = [
    <Button
      text={LanguageConverter('generic.goBack')}
      key="handle-go-back-button"
      onClick={() => setValueForModal(null)}
    />,

    <Button
      text={LanguageConverter('generic.unconfirm')}
      variant={BUTTON_VARIANT.DELETE}
      key="handle-unConfirm-button"
      onClick={handleOnUnConfirm}
    />,
  ];

  return (
    <SectionCard
      header={LanguageConverter('userDetail.waeAccess.header')}
      headerTestId="wae-onboarding-status-card-header"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <CheckBox
          onChange={(event) => handleOnChange({ onboardingDoc: event.target.checked })}
          checked={!!onboardingDoc}
          disabled={isProcessingDocumentsApproved}
          label="Onboarding Documentation Complete"
          name="onboardingDoc"
          variant="standard"
          fullWidth
          sx={{
            '& .MuiFormControlLabel-label': styles.checkBoxLabel,
          }}
        />
        <Typography sx={styles.nameStyle}>{getEVerifiedByName(onboardingDoc)}</Typography>
        <CheckBox
          onChange={(event) => handleOnChange({ eVerify: event.target.checked })}
          checked={!!eVerify}
          disabled={isProcessingEVerify}
          label="E-Verified"
          name="eVerified"
          variant="standard"
          fullWidth
          sx={{
            '& .MuiFormControlLabel-label': styles.checkBoxLabel,
          }}
        />
        <Typography sx={styles.nameStyle}>{getEVerifiedByName(eVerify)}</Typography>
        <SageWorkerControls
          selectedUserId={selectedUserId}
          sageId={sageId}
          sageEVerify={sageEVerify}
        />
      </Box>
      <ConfirmationDialog
        open={!!valueForModal}
        dialogTitle={LanguageConverter('user.onboardingDocs.unconfirm.title')}
        dialogContentText={body}
        dialogActions={dialogButtons}
        onBackdropClick={() => setValueForModal(null)}
      />
    </SectionCard>
  );
};

WAEOnboardingStatusCard.propTypes = {
  onboardingDoc: PropTypes.shape({}),
  eVerify: PropTypes.shape({}),
  sageId: PropTypes.string,
  sageEVerify: PropTypes.shape({
    status: PropTypes.string,
    caseNumber: PropTypes.string,
    eVerifier: PropTypes.string,
    verifiedOn: PropTypes.number,
  }),
  selectedUserId: PropTypes.string,
};

export default WAEOnboardingStatusCard;
